/**
 * Union project, lead, and unallocated CRM sessions into a job-costing report
 * plus an activity log. Sessions that already have a project/lead entity are
 * ignored for costing (those minutes live on projectTimeEntries / leadTimeEntries).
 */
import type {
  CrmActivitySession,
  EnhancedDeal,
  Lead,
  LeadTimeEntry,
  ProjectTimeEntry,
  TimberProject,
} from '../types';
import {
  type ActivityLogEntry,
} from './activityLogDisplayGroups';
import { getProjectBillingLabel, type ProjectBillingLabel } from './billableProjectTimeReport';
import { formatCrmMinutes } from './crmActivityHours';
import { leadDisplayName } from './crmActivityContext';

export type TeamActivityPeriod = 'week' | 'month' | 'custom';

export interface TeamActivityDateRange {
  start: Date;
  end: Date;
}

export interface TeamActivityUserMinutes {
  userId: string;
  userName: string;
  minutes: number;
}

export interface TeamActivityProjectRow {
  projectId: string;
  projectName: string;
  clientName: string;
  billingLabel: ProjectBillingLabel;
  isBillable: boolean;
  projectMinutes: number;
  leadMinutes: number;
  minutes: number;
  byUser: TeamActivityUserMinutes[];
}

export interface TeamActivityLeadRow {
  leadId: string;
  leadName: string;
  dealId?: string;
  minutes: number;
  byUser: TeamActivityUserMinutes[];
}

export interface TeamActivityPageMinutes {
  pageKey: string;
  pageLabel: string;
  minutes: number;
}

export interface TeamActivityOverheadRow {
  userId: string;
  userName: string;
  minutes: number;
  byPage: TeamActivityPageMinutes[];
}

export interface TeamActivityPersonTotal {
  userId: string;
  userName: string;
  attributedMinutes: number;
  overheadMinutes: number;
  totalMinutes: number;
}

export interface TeamActivityReport {
  range: TeamActivityDateRange;
  projects: TeamActivityProjectRow[];
  leads: TeamActivityLeadRow[];
  overhead: TeamActivityOverheadRow[];
  byPerson: TeamActivityPersonTotal[];
  activityLog: ActivityLogEntry[];
  projectMinutes: number;
  leadMinutes: number;
  overheadMinutes: number;
  attributedMinutes: number;
}

function addUserMinutes(
  map: Map<string, TeamActivityUserMinutes>,
  userId: string,
  userName: string,
  minutes: number,
): void {
  if (minutes <= 0) return;
  const uid = userId || 'unknown';
  const existing = map.get(uid);
  if (existing) {
    existing.minutes += minutes;
    if (userName && existing.userName === 'Unknown') existing.userName = userName;
    return;
  }
  map.set(uid, { userId: uid, userName: userName || 'Unknown', minutes });
}

function sortedUsers(map: Map<string, TeamActivityUserMinutes>): TeamActivityUserMinutes[] {
  return Array.from(map.values()).sort((a, b) => b.minutes - a.minutes || a.userName.localeCompare(b.userName));
}

export function entryOverlapsRange(
  startedAt: Date,
  endedAt: Date,
  range: TeamActivityDateRange,
): boolean {
  return startedAt.getTime() <= range.end.getTime() && endedAt.getTime() >= range.start.getTime();
}

export function projectEntryBounds(entry: ProjectTimeEntry): { start: Date; end: Date } {
  const start = entry.startTime ?? entry.date;
  const end = entry.endTime ?? entry.date;
  return { start, end };
}

export function leadHasTimberProject(args: {
  leadId: string;
  dealId?: string;
  projects: Array<Pick<TimberProject, 'id' | 'dealId'>>;
  deals: Array<Pick<EnhancedDeal, 'id' | 'leadId'>>;
}): boolean {
  const { leadId, dealId, projects, deals } = args;
  if (dealId && projects.some((p) => p.dealId === dealId)) return true;
  const dealIds = new Set(
    deals.filter((d) => d.leadId === leadId && d.id).map((d) => d.id as string),
  );
  if (dealId) dealIds.add(dealId);
  if (dealIds.size === 0) return false;
  return projects.some((p) => p.dealId && dealIds.has(p.dealId));
}

export function projectIdForLead(args: {
  leadId: string;
  dealId?: string;
  projects: Array<Pick<TimberProject, 'id' | 'dealId'>>;
  deals: Array<Pick<EnhancedDeal, 'id' | 'leadId'>>;
}): string | undefined {
  const { leadId, dealId, projects, deals } = args;
  if (dealId) {
    const byDeal = projects.find((p) => p.id && p.dealId === dealId);
    if (byDeal?.id) return byDeal.id;
  }
  const dealIds = new Set(
    deals.filter((d) => d.leadId === leadId && d.id).map((d) => d.id as string),
  );
  const byLeadDeal = projects.find((p) => p.id && p.dealId && dealIds.has(p.dealId));
  return byLeadDeal?.id;
}

function sessionIsJobAttributed(session: CrmActivitySession): boolean {
  return session.entityType === 'project'
    || session.entityType === 'lead'
    || session.entityType === 'deal'
    || session.entityType === 'proposal';
}

function personBucket(
  totals: Map<string, TeamActivityPersonTotal>,
  userId: string,
  userName: string,
): TeamActivityPersonTotal {
  const uid = userId || 'unknown';
  let row = totals.get(uid);
  if (!row) {
    row = {
      userId: uid,
      userName: userName || 'Unknown',
      attributedMinutes: 0,
      overheadMinutes: 0,
      totalMinutes: 0,
    };
    totals.set(uid, row);
  } else if (userName && row.userName === 'Unknown') {
    row.userName = userName;
  }
  return row;
}

export function buildTeamActivityReport(args: {
  range: TeamActivityDateRange;
  projectEntries: ProjectTimeEntry[];
  leadEntries: LeadTimeEntry[];
  sessions: CrmActivitySession[];
  projects: TimberProject[];
  leads: Lead[];
  deals: EnhancedDeal[];
}): TeamActivityReport {
  const { range, projects, leads, deals } = args;
  const projectById = new Map(projects.filter((p) => p.id).map((p) => [p.id as string, p]));
  const leadById = new Map(leads.filter((l) => l.id).map((l) => [l.id as string, l]));

  const projectEntries = args.projectEntries.filter((entry) => {
    const { start, end } = projectEntryBounds(entry);
    return entryOverlapsRange(start, end, range);
  });
  const leadEntries = args.leadEntries.filter((entry) =>
    entryOverlapsRange(entry.startTime, entry.endTime, range),
  );
  const sessions = args.sessions.filter((session) =>
    entryOverlapsRange(session.startedAt, session.endedAt, range),
  );

  type ProjectAcc = {
    projectMinutes: number;
    leadMinutes: number;
    users: Map<string, TeamActivityUserMinutes>;
  };
  const projectAcc = new Map<string, ProjectAcc>();

  const ensureProject = (projectId: string): ProjectAcc => {
    let acc = projectAcc.get(projectId);
    if (!acc) {
      acc = { projectMinutes: 0, leadMinutes: 0, users: new Map() };
      projectAcc.set(projectId, acc);
    }
    return acc;
  };

  for (const entry of projectEntries) {
    const acc = ensureProject(entry.projectId);
    acc.projectMinutes += entry.minutes;
    addUserMinutes(acc.users, entry.userId, entry.userName, entry.minutes);
  }

  type LeadAcc = {
    leadId: string;
    dealId?: string;
    minutes: number;
    users: Map<string, TeamActivityUserMinutes>;
  };
  const leadAcc = new Map<string, LeadAcc>();

  for (const entry of leadEntries) {
    const linkedProjectId = projectIdForLead({
      leadId: entry.leadId,
      dealId: entry.dealId,
      projects,
      deals,
    });
    if (linkedProjectId) {
      const acc = ensureProject(linkedProjectId);
      acc.leadMinutes += entry.minutes;
      addUserMinutes(acc.users, entry.userId, entry.userName, entry.minutes);
      continue;
    }
    const key = entry.leadId || entry.dealId || 'unknown-lead';
    let acc = leadAcc.get(key);
    if (!acc) {
      acc = { leadId: entry.leadId, dealId: entry.dealId, minutes: 0, users: new Map() };
      leadAcc.set(key, acc);
    }
    acc.minutes += entry.minutes;
    addUserMinutes(acc.users, entry.userId, entry.userName, entry.minutes);
  }

  type OverheadAcc = {
    userId: string;
    userName: string;
    minutes: number;
    pages: Map<string, TeamActivityPageMinutes>;
  };
  const overheadAcc = new Map<string, OverheadAcc>();

  for (const session of sessions) {
    if (sessionIsJobAttributed(session)) continue;
    const uid = session.userId || 'unknown';
    let acc = overheadAcc.get(uid);
    if (!acc) {
      acc = { userId: uid, userName: session.userName || 'Unknown', minutes: 0, pages: new Map() };
      overheadAcc.set(uid, acc);
    }
    acc.minutes += session.minutes;
    const page = acc.pages.get(session.pageKey);
    if (page) page.minutes += session.minutes;
    else {
      acc.pages.set(session.pageKey, {
        pageKey: session.pageKey,
        pageLabel: session.pageLabel,
        minutes: session.minutes,
      });
    }
  }

  const projectRows: TeamActivityProjectRow[] = Array.from(projectAcc.entries()).map(([projectId, acc]) => {
    const proj = projectById.get(projectId);
    const billingLabel = getProjectBillingLabel(proj);
    return {
      projectId,
      projectName: proj?.name || 'Unknown project',
      clientName: proj?.clientName || '',
      billingLabel,
      isBillable: billingLabel === 'Billable',
      projectMinutes: acc.projectMinutes,
      leadMinutes: acc.leadMinutes,
      minutes: acc.projectMinutes + acc.leadMinutes,
      byUser: sortedUsers(acc.users),
    };
  }).sort((a, b) => b.minutes - a.minutes || a.projectName.localeCompare(b.projectName));

  const leadRows: TeamActivityLeadRow[] = Array.from(leadAcc.values()).map((acc) => ({
    leadId: acc.leadId,
    leadName: leadDisplayName(leadById.get(acc.leadId)),
    dealId: acc.dealId,
    minutes: acc.minutes,
    byUser: sortedUsers(acc.users),
  })).sort((a, b) => b.minutes - a.minutes || a.leadName.localeCompare(b.leadName));

  const overheadRows: TeamActivityOverheadRow[] = Array.from(overheadAcc.values()).map((acc) => ({
    userId: acc.userId,
    userName: acc.userName,
    minutes: acc.minutes,
    byPage: Array.from(acc.pages.values()).sort((a, b) => b.minutes - a.minutes || a.pageLabel.localeCompare(b.pageLabel)),
  })).sort((a, b) => b.minutes - a.minutes || a.userName.localeCompare(b.userName));

  const people = new Map<string, TeamActivityPersonTotal>();
  for (const row of projectRows) {
    for (const user of row.byUser) {
      const bucket = personBucket(people, user.userId, user.userName);
      bucket.attributedMinutes += user.minutes;
    }
  }
  for (const row of leadRows) {
    for (const user of row.byUser) {
      const bucket = personBucket(people, user.userId, user.userName);
      bucket.attributedMinutes += user.minutes;
    }
  }
  for (const row of overheadRows) {
    const bucket = personBucket(people, row.userId, row.userName);
    bucket.overheadMinutes += row.minutes;
  }
  const byPerson = Array.from(people.values()).map((row) => ({
    ...row,
    totalMinutes: row.attributedMinutes + row.overheadMinutes,
  })).sort((a, b) => b.totalMinutes - a.totalMinutes || a.userName.localeCompare(b.userName));

  const activityLog: ActivityLogEntry[] = [];

  for (const entry of projectEntries) {
    const { start, end } = projectEntryBounds(entry);
    const proj = projectById.get(entry.projectId);
    activityLog.push({
      id: entry.id || `project-${entry.projectId}-${start.getTime()}`,
      source: 'project',
      userId: entry.userId,
      userName: entry.userName,
      minutes: entry.minutes,
      startedAt: start,
      endedAt: end,
      pageLabel: 'Project Hub',
      entityLabel: proj?.name,
      type: entry.type === 'explicit' ? 'explicit' : 'implied',
      note: entry.note,
    });
  }

  for (const entry of leadEntries) {
    activityLog.push({
      id: entry.id || `lead-${entry.leadId}-${entry.startTime.getTime()}`,
      source: 'lead',
      userId: entry.userId,
      userName: entry.userName,
      minutes: entry.minutes,
      startedAt: entry.startTime,
      endedAt: entry.endTime,
      pageLabel: 'Lead / proposal',
      entityLabel: leadDisplayName(leadById.get(entry.leadId)),
      type: entry.type === 'explicit' ? 'explicit' : 'implied',
      note: entry.note,
    });
  }

  for (const session of sessions) {
    activityLog.push({
      id: session.id || `crm-${session.userId}-${session.startedAt.getTime()}`,
      source: 'crm',
      userId: session.userId,
      userName: session.userName,
      minutes: session.minutes,
      startedAt: session.startedAt,
      endedAt: session.endedAt,
      pageLabel: session.pageLabel,
      entityLabel: session.entityName,
      type: 'implied',
    });
  }

  activityLog.sort((a, b) => b.startedAt.getTime() - a.startedAt.getTime());

  const projectMinutes = projectRows.reduce((sum, row) => sum + row.projectMinutes, 0);
  const linkedLeadMinutes = projectRows.reduce((sum, row) => sum + row.leadMinutes, 0);
  const unlinkedLeadMinutes = leadRows.reduce((sum, row) => sum + row.minutes, 0);
  const leadMinutes = linkedLeadMinutes + unlinkedLeadMinutes;
  const overheadMinutes = overheadRows.reduce((sum, row) => sum + row.minutes, 0);

  return {
    range,
    projects: projectRows,
    leads: leadRows,
    overhead: overheadRows,
    byPerson,
    activityLog,
    projectMinutes,
    leadMinutes,
    overheadMinutes,
    attributedMinutes: projectMinutes + leadMinutes,
  };
}

export function teamActivityPeriodRange(
  period: Exclude<TeamActivityPeriod, 'custom'>,
  now: Date = new Date(),
): TeamActivityDateRange {
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  if (period === 'week') {
    const weekday = start.getDay();
    const mondayOffset = weekday === 0 ? -6 : 1 - weekday;
    start.setDate(start.getDate() + mondayOffset);
  } else {
    start.setDate(1);
  }
  return { start, end };
}

export function formatActivityHours(minutes: number): string {
  return formatCrmMinutes(minutes);
}

export function buildTeamActivityCsv(report: TeamActivityReport): string {
  const lines = [
    ['Section', 'Job / page', 'Client', 'Billing', 'Person', 'Hours', 'Minutes'].join(','),
  ];
  const cell = (value: string | number) => {
    const text = String(value ?? '');
    if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
    return text;
  };
  const push = (cols: Array<string | number>) => {
    lines.push(cols.map(cell).join(','));
  };

  for (const row of report.projects) {
    if (row.byUser.length === 0) {
      push(['Project', row.projectName, row.clientName, row.billingLabel, '', formatActivityHours(row.minutes), Math.round(row.minutes)]);
      continue;
    }
    for (const user of row.byUser) {
      push(['Project', row.projectName, row.clientName, row.billingLabel, user.userName, formatActivityHours(user.minutes), Math.round(user.minutes)]);
    }
  }
  for (const row of report.leads) {
    for (const user of row.byUser) {
      push(['Lead', row.leadName, '', 'Lead / proposal', user.userName, formatActivityHours(user.minutes), Math.round(user.minutes)]);
    }
  }
  for (const row of report.overhead) {
    for (const page of row.byPage) {
      push(['Overhead', page.pageLabel, '', 'Unallocated', row.userName, formatActivityHours(page.minutes), Math.round(page.minutes)]);
    }
  }
  return `${lines.join('\n')}\n`;
}

export async function fetchTeamActivitySources(range: TeamActivityDateRange): Promise<{
  projectEntries: ProjectTimeEntry[];
  leadEntries: LeadTimeEntry[];
  sessions: CrmActivitySession[];
}> {
  const { getProjectTimeEntriesInRange } = await import('./projectTimeTrackingService');
  const { getLeadTimeEntriesInRange } = await import('./leadTimeTrackingService');
  const { fetchCrmActivitySessionsInRange } = await import('./crmActivitySessionService');
  const [projectEntries, leadEntries, sessions] = await Promise.all([
    getProjectTimeEntriesInRange(range),
    getLeadTimeEntriesInRange(range),
    fetchCrmActivitySessionsInRange(range),
  ]);
  return { projectEntries, leadEntries, sessions };
}
